import { PRODUCT_TYPE, SUBSCRIBE_STATUS } from 'configs/constants';
import {
  SUBSCRIPTION_CATEGORY,
  SUBSCRIPTION_TYPE,
  TDeviceResponseType,
  TPlanList,
  TProductType,
  TSubscriptionType
} from 'typings';

export function getPlanDetails(
  planLevel: number,
  subscriptionCycle: number
): string {
  // Determine the plan type based on the subscription cycle
  let planType: string;
  if (subscriptionCycle === 1) {
    planType = 'monthly';
  } else if (subscriptionCycle === 12) {
    planType = 'yearly';
  } else {
    planType = '';
  }
  return `${planType}_${planLevel}`;
}

export function getProductTypeFromDevice({
  deviceId,
  devices
}: {
  devices: TDeviceResponseType[];
  deviceId: string;
}): TProductType {
  return (
    devices.find((device) => device.id === deviceId)?.model_product_type ||
    'HomeSecurity'
  );
}

export function getProductTypeFromSubscription({
  subscriptions,
  subscriptionId
}: {
  subscriptions: TSubscriptionType[];
  subscriptionId: string;
}): string {
  return (
    subscriptions.find((subscription) => subscription.id === subscriptionId)
      ?.product_id || 'HomeSecurity'
  );
}

export function getAllDevicesByProductType(
  devices: TDeviceResponseType[],
  productType: TProductType
): TDeviceResponseType[] {
  return devices.filter((device) => device.model_product_type === productType);
}

export function getAllSubscriptionsByProductType(
  subscriptions: TSubscriptionType[],
  productType: TProductType
): TSubscriptionType[] {
  return subscriptions.filter(
    (subscription) =>
      // Backward compatibility: Data subscriptions are space-scoped and are
      // bucketed by sku_category, but currently still send product_id as
      // HomeSecurity. Exclude them here so they don't leak into product-wise
      // lists (e.g. HomeSecurity).
      subscription.sku_category !== SUBSCRIPTION_CATEGORY.DATA &&
      subscription.product_id === productType
  );
}

/**
 * Data auto top-up discount eligibility: the user must hold a paid Data or Combo
 * base plan. Used to decide whether bundle-discount pricing is shown for a user
 * who does not yet have an auto top-up.
 */
export function isDataAutoTopUpDiscountEligible(
  subscriptions: TSubscriptionType[]
): boolean {
  // Eligible if the user holds any paid Data or Combo base plan. We check every
  // subscription (not just the first match) so a paid Data plan still qualifies
  // even when, say, a trial Combo plan appears earlier in the list.
  return subscriptions.some(
    (subscription) =>
      (subscription.sku_category === SUBSCRIPTION_CATEGORY.DATA ||
        subscription.sku_category === SUBSCRIPTION_CATEGORY.COMBO) &&
      subscription.subscription_type === SUBSCRIPTION_TYPE.PAID
  );
}

/**
 * The top-tier plan in a list — highest `level`, and within a level the
 * longer billing cycle. Used to quote plan-wide facts (e.g. the free-trial
 * length) before the user has picked a tier, the same way the product plan
 * pages do.
 */
export function getHighestLevelPlan(plans: TPlanList[]): TPlanList | null {
  if (!plans.length) return null;
  return plans.reduce(
    (max, plan) =>
      plan.level > max.level ||
      (plan.level === max.level &&
        plan.subscription_cycle > max.subscription_cycle)
        ? plan
        : max,
    plans[0]
  );
}

/**
 * The user's real (Trial/Paid) Home Security subscription, if any.
 *
 * This screen only creates *initial* purchases. A Preview subscription is the
 * free tier the user is meant to upgrade FROM, so buying here is allowed. But a
 * real active subscription (Trial/Paid) makes POST /purchases 400 ("User Space
 * has active subscription") — that user should be modifying their existing
 * plan, not creating a new one, so they're sent to the upgrade screen. The
 * plan-selection experiment uses the same check to leave them out.
 */
export const findActiveNonPreviewSubscription = (
  subscriptions: TSubscriptionType[]
) =>
  subscriptions.find(
    (sub) =>
      sub.subscribe_status === SUBSCRIBE_STATUS.Active &&
      sub.subscription_type !== SUBSCRIPTION_TYPE.PREVIEW
  );

/** `device_type` atlas sends for a hub (its cameras carry `parent_device_id`). */
export const HUB_DEVICE_TYPE = 'Hub';

/** Whether the device is a hub. A missing `device_type` reads as a camera. */
export function isHubDevice(device?: TDeviceResponseType): boolean {
  return device?.device_type === HUB_DEVICE_TYPE;
}

/** SKU feature that marks a Home Security plan as covering a hub. */
export const HUB_PLAN_FEATURE_ID = 'hubs_supported';

/** Whether the space has a Home Security hub among its devices. */
export function hasHomeSecurityHub(devices: TDeviceResponseType[]): boolean {
  return (devices ?? []).some(
    (device) =>
      isHubDevice(device) &&
      device.model_product_type === PRODUCT_TYPE.HOME_SECURITY
  );
}

/**
 * The plans a space may pick from. With a Home Security hub in the space only
 * SKUs that list the hub feature can cover it, so the rest are dropped;
 * without one the list is returned untouched.
 */
export function filterPlansForHub(
  plans: TPlanList[],
  hasHub: boolean
): TPlanList[] {
  if (!hasHub) return plans;
  return plans.filter((plan) =>
    (plan.supported_features ?? []).some(
      (feature) => feature.id === HUB_PLAN_FEATURE_ID
    )
  );
}
